/**
 * The statement that a confidentiality label grants no protection (PRD §5.10).
 *
 * Its own component rather than a `Note`, and the distinction is not cosmetic.
 * A `Note` is explicitly *"an aside — a clarification the reader needs at this
 * point, not a status"*, sitting on the page's own off-white so it reads as a
 * footnote. This is the opposite: it corrects an assumption the reader has
 * almost certainly already made. Someone who sees a file marked CONFIDENTIAL
 * concludes it is restricted, and the plan is explicit that *"implying
 * protection that does not exist is worse than not marking it"*. A footnote
 * would be skimmed, which is exactly the failure the requirement exists to
 * prevent.
 *
 * So it carries semantic colour and a border, and it is placed where the
 * assumption forms rather than where it would be tidy: above the upload form
 * while someone is deciding whether to upload a document at all, and above the
 * document list where the labels are on display.
 *
 * Not `role="alert"`. It is present on load rather than appearing in response
 * to anything, and an alert that fires on every page view is noise a screen
 * reader user learns to tune out. The text is in the reading order where it
 * matters instead.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

export interface LabelOnlyNoticeProps {
  /**
   * `short` for a place where the full sentence would crowd the layout — a
   * table's caption area, say. The full text is the default because this is
   * the one statement the requirement asks for in full.
   */
  variant?: 'full' | 'short';
  className?: string;
}

export function LabelOnlyNotice({
  variant = 'full',
  className,
}: LabelOnlyNoticeProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <p
      className={cn(
        'bg-sem-warn-bg text-navy-text border-sem-warn rounded-md border-l-2',
        'px-3.5 py-3 text-xs leading-relaxed',
        className,
      )}
    >
      {variant === 'short'
        ? t('attachment.labelNoticeShort')
        : t('attachment.labelNotice')}
    </p>
  );
}
